import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, ShieldCheck, Camera } from 'lucide-react';

const STORAGE_KEY = 'archivio.onboarded';

interface Slide {
  icon: ReactNode;
  title: string;
  sub: string;
}

const SLIDES: Slide[] = [
  {
    icon: <Archive size={44} />,
    title: 'Tutti i tuoi documenti, in un posto solo',
    sub: "Carte d'identità, patenti, garanzie e scontrini: pronti da recuperare e condividere in pochi secondi.",
  },
  {
    icon: <ShieldCheck size={44} />,
    title: 'Solo tu puoi aprirli',
    sub: 'Dati cifrati e sblocco con Face ID o PIN. I tuoi documenti restano privati, sempre.',
  },
  {
    icon: <Camera size={44} />,
    title: 'Aggiungi il primo documento',
    sub: 'Scansiona un documento per iniziare. Niente OCR: i dati li inserisci tu, in modo semplice.',
  },
];

function markDone() {
  try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignora */ }
}

/** Gate primo avvio (§6.17–6.18). Mostra l'onboarding una sola volta. */
export function OnboardingGate({ children }: { children: ReactNode }) {
  const [done, setDone] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return true; }
  });
  if (done) return <>{children}</>;
  return (
    <>
      {children}
      <Onboarding onClose={() => { markDone(); setDone(true); }} />
    </>
  );
}

function Onboarding({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const slide = SLIDES[step];
  const last = step === SLIDES.length - 1;

  const next = () => (last ? onClose() : setStep((s) => s + 1));

  return (
    <div className="onboard">
      <button className="onboard__skip" style={{ position: 'absolute', top: 22, right: 22 }} onClick={onClose}>
        Salta
      </button>

      <div className="onboard__icon">{slide.icon}</div>
      <h1 className="onboard__title">{slide.title}</h1>
      <p className="onboard__sub">{slide.sub}</p>

      <div className="onboard__dots" role="tablist" aria-label="Avanzamento">
        {SLIDES.map((_, i) => (
          <span key={i} className={`onboard__dot${i === step ? ' is-active' : ''}`} />
        ))}
      </div>

      <div className="onboard__actions">
        {last ? (
          <>
            <button className="btn btn--accent btn--block" onClick={() => { onClose(); navigate('/scansiona'); }}>
              <Camera size={18} /> Scansiona documento
            </button>
            <button className="onboard__skip" onClick={onClose}>Lo farò più tardi</button>
          </>
        ) : (
          <button className="btn btn--accent btn--block" onClick={next}>Continua</button>
        )}
      </div>
    </div>
  );
}
